import { apiClient, toServiceError } from '@/services/http/client'
import type {
  AssistantWorkItem,
  CreateAssistantWorkInput,
  CreateResearchMaterialInput,
  ResearchCitation,
  ResearchMaterial,
} from '@/types/assistantWork.types'
import type { AssistantWorkService } from './assistantWork.service'

const BASE = '/tax-assessments'

function mapCitation(raw: Record<string, unknown>): ResearchCitation {
  return {
    sourceTitle: String(raw.source_title ?? ''),
    authority: String(raw.authority ?? ''),
    sourceUrl: raw.source_url ? String(raw.source_url) : undefined,
    citationText: String(raw.citation_text ?? ''),
  }
}

function mapWork(raw: Record<string, unknown>): AssistantWorkItem {
  return {
    id: String(raw.id),
    tenantId: String(raw.tenant_id),
    assessmentId: raw.assessment_id ? String(raw.assessment_id) : undefined,
    customerId: raw.customer_id ? String(raw.customer_id) : undefined,
    workType: raw.work_type as AssistantWorkItem['workType'],
    assignedPersona: raw.assigned_persona as AssistantWorkItem['assignedPersona'],
    title: String(raw.title),
    instructions: String(raw.instructions),
    assignedToUserId: String(raw.assigned_to_user_id),
    assignedByUserId: String(raw.assigned_by_user_id),
    branchId: raw.branch_id ? String(raw.branch_id) : undefined,
    teamId: raw.team_id ? String(raw.team_id) : undefined,
    portfolioId: raw.portfolio_id ? String(raw.portfolio_id) : undefined,
    portfolioOwnerUserId: raw.portfolio_owner_user_id ? String(raw.portfolio_owner_user_id) : undefined,
    dueAt: raw.due_at ? String(raw.due_at) : undefined,
    artifactDocumentId: raw.artifact_document_id ? String(raw.artifact_document_id) : undefined,
    queryText: raw.query_text ? String(raw.query_text) : undefined,
    responseText: raw.response_text ? String(raw.response_text) : undefined,
    handoffNotes: raw.handoff_notes ? String(raw.handoff_notes) : undefined,
    status: raw.status as AssistantWorkItem['status'],
    reviewerId: raw.reviewer_id ? String(raw.reviewer_id) : undefined,
    riskLevel: String(raw.risk_level ?? 'standard'),
    workValue: Number(raw.work_value ?? 0),
    reviewPolicyId: raw.review_policy_id ? String(raw.review_policy_id) : undefined,
    submittedAt: raw.submitted_at ? String(raw.submitted_at) : undefined,
    escalationDueAt: raw.escalation_due_at ? String(raw.escalation_due_at) : undefined,
    reviewNotes: raw.review_notes ? String(raw.review_notes) : undefined,
    reviewedAt: raw.reviewed_at ? String(raw.reviewed_at) : undefined,
    createdAt: String(raw.created_at),
    updatedAt: String(raw.updated_at),
  }
}

function mapResearch(raw: Record<string, unknown>): ResearchMaterial {
  return {
    id: String(raw.id),
    tenantId: String(raw.tenant_id),
    workItemId: raw.work_item_id ? String(raw.work_item_id) : undefined,
    assessmentId: raw.assessment_id ? String(raw.assessment_id) : undefined,
    customerId: raw.customer_id ? String(raw.customer_id) : undefined,
    title: String(raw.title),
    analysis: String(raw.analysis),
    advisoryDraft: String(raw.advisory_draft),
    citations: ((raw.citations as Record<string, unknown>[]) ?? []).map(mapCitation),
    jurisdiction: String(raw.jurisdiction),
    assessmentYear: raw.assessment_year ? String(raw.assessment_year) : undefined,
    effectiveFrom: String(raw.effective_from),
    effectiveTo: raw.effective_to ? String(raw.effective_to) : undefined,
    supersedesId: raw.supersedes_id ? String(raw.supersedes_id) : undefined,
    status: raw.status as ResearchMaterial['status'],
    createdBy: String(raw.created_by),
    branchId: raw.branch_id ? String(raw.branch_id) : undefined,
    teamId: raw.team_id ? String(raw.team_id) : undefined,
    portfolioId: raw.portfolio_id ? String(raw.portfolio_id) : undefined,
    portfolioOwnerUserId: raw.portfolio_owner_user_id ? String(raw.portfolio_owner_user_id) : undefined,
    riskLevel: String(raw.risk_level ?? 'standard'),
    workValue: Number(raw.work_value ?? 0),
    reviewPolicyId: raw.review_policy_id ? String(raw.review_policy_id) : undefined,
    submittedAt: raw.submitted_at ? String(raw.submitted_at) : undefined,
    escalationDueAt: raw.escalation_due_at ? String(raw.escalation_due_at) : undefined,
    reviewerId: raw.reviewer_id ? String(raw.reviewer_id) : undefined,
    reviewNotes: raw.review_notes ? String(raw.review_notes) : undefined,
    reviewedAt: raw.reviewed_at ? String(raw.reviewed_at) : undefined,
    createdAt: String(raw.created_at),
    updatedAt: String(raw.updated_at),
  }
}

function workBody(input: CreateAssistantWorkInput) {
  return {
    assessment_id: input.assessmentId,
    customer_id: input.customerId,
    work_type: input.workType,
    assigned_persona: input.assignedPersona,
    assigned_to_user_id: input.assignedToUserId,
    title: input.title,
    instructions: input.instructions,
    due_at: input.dueAt,
    branch_id: input.branchId,
    team_id: input.teamId,
    portfolio_id: input.portfolioId,
    portfolio_owner_user_id: input.portfolioOwnerUserId,
    reviewer_id: input.reviewerId,
    risk_level: input.riskLevel,
    work_value: input.workValue,
  }
}

function researchBody(input: CreateResearchMaterialInput) {
  return {
    work_item_id: input.workItemId,
    assessment_id: input.assessmentId,
    customer_id: input.customerId,
    title: input.title,
    analysis: input.analysis,
    advisory_draft: input.advisoryDraft,
    citations: input.citations.map((citation) => ({
      source_title: citation.sourceTitle,
      authority: citation.authority,
      source_url: citation.sourceUrl,
      citation_text: citation.citationText,
    })),
    jurisdiction: input.jurisdiction,
    assessment_year: input.assessmentYear,
    effective_from: input.effectiveFrom,
    effective_to: input.effectiveTo,
    supersedes_id: input.supersedesId,
    branch_id: input.branchId,
    team_id: input.teamId,
    portfolio_id: input.portfolioId,
    portfolio_owner_user_id: input.portfolioOwnerUserId,
    risk_level: input.riskLevel,
    work_value: input.workValue,
  }
}

export function createHttpAssistantWorkService(): AssistantWorkService {
  return {
    async listWork(status, persona) {
      try {
        const response = await apiClient.get(`${BASE}/assistant-work`, {
          params: { status, assigned_persona: persona },
        })
        return (response.data.items as Record<string, unknown>[]).map(mapWork)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async createWork(input) {
      try {
        return mapWork((await apiClient.post(`${BASE}/assistant-work`, workBody(input))).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async updateWork(id, input) {
      try {
        const body = {
          artifact_document_id: input.artifactDocumentId,
          query_text: input.queryText,
          response_text: input.responseText,
          handoff_notes: input.handoffNotes,
        }
        return mapWork((await apiClient.patch(`${BASE}/assistant-work/${id}`, body)).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async submitWork(id, handoffNotes) {
      try {
        return mapWork((await apiClient.post(`${BASE}/assistant-work/${id}/submit`, {
          handoff_notes: handoffNotes,
        })).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async reviewWork(id, decision, notes) {
      try {
        return mapWork((await apiClient.post(`${BASE}/assistant-work/${id}/review`, {
          decision,
          notes,
        })).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async listResearch(status) {
      try {
        const response = await apiClient.get(`${BASE}/research-materials`, { params: { status } })
        return (response.data.items as Record<string, unknown>[]).map(mapResearch)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async createResearch(input) {
      try {
        return mapResearch((await apiClient.post(
          `${BASE}/research-materials`,
          researchBody(input),
        )).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async submitResearch(id) {
      try {
        return mapResearch((await apiClient.post(
          `${BASE}/research-materials/${id}/submit`,
        )).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
    async reviewResearch(id, decision, notes) {
      try {
        return mapResearch((await apiClient.post(
          `${BASE}/research-materials/${id}/review`,
          { decision, notes },
        )).data)
      } catch (error) {
        throw toServiceError(error)
      }
    },
  }
}
